/**
 * Daily Rewards card on the Profile page (specs/profile-page AC-13..16).
 *
 * Reads its display state straight from the shared economy store — the SAME store the menu's
 * DailyRewardModal reads — so claiming in either place keeps both in sync and no double-claim
 * is possible (AC-16 / T-9). The actual claim side-effects (guest guard, sound, snackbar) are
 * injected via `onCollect`, owned by the screen.
 */

import React from 'react';
import { View, Text, Platform } from 'react-native';
import { Check, Flame, Lock } from 'lucide-react-native';
import { useEconomyStore } from '../../../shared/stores/economyStore';
import { useCountdownToDate } from '../../../challenges/hooks/useCountdownToDate';
import { SoundPressable } from '../../../shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../../shared/constants/sounds.constants';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations';
import { PROFILE_COLORS, PROFILE_TEST_IDS } from '../ProfileScreen/ProfileScreen.constants';
import { deriveDailyLadder } from './DailyRewardsCard.helper';
import { dailyRewardsCardStyles as styles } from './DailyRewardsCard.styles';

export interface DailyRewardsCardProps {
  /** Fired when the user taps Collect and a reward is claimable. */
  onCollect: () => void;
  testID?: string;
}

export const DailyRewardsCard: React.FC<DailyRewardsCardProps> = ({ onCollect, testID }) => {
  const dailyClaimStreak = useEconomyStore((s) => s.dailyClaimStreak);
  const lastDailyClaimAt = useEconomyStore((s) => s.lastDailyClaimAt);
  const nextClaimAt = useEconomyStore((s) => s.nextClaimAt);
  const isClaiming = useEconomyStore((s) => s.isClaiming);
  const { segments } = useCountdownToDate(nextClaimAt);

  const ladder = deriveDailyLadder(dailyClaimStreak, lastDailyClaimAt, nextClaimAt);
  const canCollect = ladder.canClaim && !isClaiming;

  const collectLabel = isClaiming
    ? 'Collecting…'
    : ladder.canClaim
      ? `Collect Day ${ladder.currentDay} · +${ladder.todayReward}`
      : ladder.weekComplete
        ? 'All 7 days collected 🎉'
        : segments
          ? `Next reward in ${segments.hours}h ${String(segments.minutes).padStart(2, '0')}m`
          : 'Come back tomorrow';

  return (
    <View style={styles.card} testID={testID ?? PROFILE_TEST_IDS.dailyCard}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <Flame size={16} color={PROFILE_COLORS.gold} fill={PROFILE_COLORS.gold} />
          <Text style={styles.headerTitle}>Daily Rewards</Text>
        </View>
        <Text style={styles.headerCount}>
          <Text style={styles.headerCountStrong}>{ladder.collectedCount}</Text>/7 collected
        </Text>
      </View>

      <View style={styles.ladderRow}>
        {ladder.days.map((d) => (
          <View key={d.day} style={styles.dayCol}>
            <Text style={[styles.dayLabel, (d.claimed || d.today) && styles.dayLabelActive]}>
              Day {d.day}
            </Text>
            <View
              style={[
                styles.node,
                d.claimed && styles.nodeClaimed,
                d.today && styles.nodeToday,
              ]}
            >
              {d.claimed ? (
                <Check size={18} color="#14093A" strokeWidth={3} />
              ) : d.today ? (
                <View style={styles.coinDot} />
              ) : (
                <Lock size={14} color={PROFILE_COLORS.lockGrey} strokeWidth={2.4} />
              )}
            </View>
            <Text
              style={[
                styles.dayAmount,
                d.claimed && styles.dayAmountClaimed,
                d.today && styles.dayAmountToday,
              ]}
            >
              +{d.amount}
            </Text>
          </View>
        ))}
      </View>

      <SoundPressable
        tapSoundId={SOUND_IDS.UI_TAP_NAV}
        onPress={onCollect}
        disabled={!canCollect}
        accessibilityRole="button"
        accessibilityLabel={collectLabel}
        testID={PROFILE_TEST_IDS.dailyCollect}
        style={({ pressed, hovered }) => [
          canCollect ? styles.collectBtn : styles.collectBtnDisabled,
          canCollect && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          canCollect && pressed && styles.collectBtnPressed,
          canCollect && hovered && Platform.OS === 'web' && styles.collectBtnHover,
        ]}
      >
        <Text style={canCollect ? styles.collectText : styles.collectTextDisabled}>{collectLabel}</Text>
      </SoundPressable>
    </View>
  );
};
